/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import clsx from 'clsx';
import { Form as AntdForm, Row, Col } from 'antd';
import isObject from 'lodash/isObject';

import * as utils from '@utils/form';

import s from './styles.module.less';

const Item = ({ className, /* omit this props */ serialize, deserialize, deserializeError, ...props }) => (
  <AntdForm.Item className={clsx(className, s.custom)} {...props} />
);

const ItemWithExtra = ({ className, name, children, extra, gutter = 20, wrapperCol, extraCol, rules, ...props }) => (
  <Item className={clsx(className, s.custom)} {...props}>
    <Row align="middle" gutter={gutter} style={{ flexWrap: 'nowrap' }}>
      <Col flex="1" {...wrapperCol}>
        <Item name={name} rules={rules} noStyle>
          {children}
        </Item>
      </Col>
      {extra && (
        <Col flex="0 0 auto" {...extraCol}>
          {extra}
        </Col>
      )}
    </Row>
  </Item>
);

const ListWithRules = ({ rules, name, ...props }) => {
  const listRules = React.useMemo(
    () =>
      rules?.map((rule) => {
        const isFieldListRule = rule.min ?? rule.max ?? rule.len;

        return isFieldListRule ? utils.fieldListValidator(rule) : rule;
      }),
    [rules],
  );

  return (
    <Item
      name={name}
      rules={listRules}
      shouldUpdate={(prevValues, currentValues) => prevValues[name] !== currentValues[name]}
      {...props}
    />
  );
};

const List = ({ children, ...props }) => {
  const list = <AntdForm.List name={props.name}>{children}</AntdForm.List>;

  if (props.rules) {
    return <ListWithRules {...props}>{list}</ListWithRules>;
  }

  return list;
};

// Reexport because there is some issue with reassigning Form.Item which hangs page
const Form = ({ onFinish, onSerializedFinish, ...props }) => {
  const handleFinish = React.useCallback(
    (values) => {
      // eslint-disable-next-line no-unused-expressions
      onFinish?.(values);

      if (onSerializedFinish) {
        const serializedValues = props.form.serialize(values);
        onSerializedFinish(serializedValues);
      }
    },
    [onFinish, onSerializedFinish, props.form],
  );

  return <AntdForm hideRequiredMark onFinish={handleFinish} {...props} />;
};

/**
 * A little hack component that react on field change
 * The problem is that we need conditionally render the blocks of related content in create articles which includes title and items
 * We are updating array field with form.setFieldsValue because it is done outside of array field context - in content module picker
 * So we can't use methods provided by array filed
 * In order to react on values change we can listen for form values using form onValuesChange and conditionally render the related content
 * But there is design choice in form library to not trigger onValuesChange if setFieldsValue was called programmatically by user.
 * More info here https://github.com/react-component/field-form#-setfields-not-trigger-onfieldschange-and-setfieldsvalue-not-trigger-onvalueschange
 * But getFieldValue used like this correctly reacts on seFieldsValue maybe because of shouldUpdate prop
 */
const WithFieldValue = ({ name, children }) => (
  <Form.Item key={name} noStyle shouldUpdate={(prevValues, currentValues) => prevValues[name] !== currentValues[name]}>
    {(form) => {
      const value = form.getFieldValue(name);

      return children(value, form);
    }}
  </Form.Item>
);

const useForm = (fieldNamesOrDefs, defaultValues) => {
  const [form] = AntdForm.useForm();

  const fields = React.useMemo(
    () => (isObject(fieldNamesOrDefs) ? Object.values(fieldNamesOrDefs) : fieldNamesOrDefs),
    [fieldNamesOrDefs],
  );

  const serialize = React.useCallback(
    (values) => {
      return fields ? utils.serializeFormValues(values, fields) : values;
    },
    [fields],
  );

  const deserialize = React.useCallback(
    (data) => ({
      ...defaultValues,
      ...utils.deserializeFormValues(data || {}, fields),
    }),
    [fields, defaultValues],
  );

  const remoteSubmit = React.useCallback(async () => {
    const formValues = await form.validateFields();
    return serialize(formValues);
  }, [form, serialize]);

  const setApiErrors = React.useCallback(
    (apiError) => {
      const fieldsWithErrors = utils.transformApiFormErrors(apiError?.json?.errors, fields);
      form.setFields(fieldsWithErrors);
    },
    [fields, form],
  );
  const setUntouched = React.useCallback(
    (names) => form.setFields(Object.keys(form.getFieldsValue(names)).map((name) => ({ name, touched: false }))),
    [form],
  );

  const wrapForm = React.useMemo(
    () => ({ ...form, serialize, deserialize, remoteSubmit, setApiErrors, setUntouched }),
    [deserialize, form, remoteSubmit, serialize, setApiErrors, setUntouched],
  );

  return [wrapForm];
};

Form.Item = Item;
Form.ItemWithExtra = ItemWithExtra;
Form.WithFieldValue = WithFieldValue;
Form.List = List;
Form.useForm = useForm;

export default Form;
